/**
 * The one rule for a song's SUMMARY, shared by every way a song is made (AUTO-092).
 *
 * A summary describes the LYRICS only: the people and what happens to them. No song structure, no
 * instruments or gear, no key or tempo, no description of how the voice sounds, no genre label.
 *
 * It was fixed again and again as a prompt rule in one path at a time, and the model kept breaking it, so
 * this is the text for every prompt AND a deterministic check that every save path can run. A word is only a
 * violation when it is NOT in the sung lyrics: a story about a man who pawns his guitar may say "guitar"
 * when the lyrics do, and "burned the bridge" is fine when the lyrics burn one.
 *
 * Isomorphic: imported by the browser (RiffWriter prompts) and the server (save paths).
 */

/** Put this in every prompt that asks a model for a summary. */
export const SUMMARY_RULE = `SUMMARY is the story the LYRICS tell and nothing else: 1-3 sentences about the people and what happens to them. Use only facts that are in the sung lyrics. Never write about how the song is built or how it sounds: no sections or order (verse, chorus, bridge, hook, intro, outro, solo, "the song", "the track", "the narrator", "the lyrics"), no instruments or gear, no description of the voice, no key, tempo, BPM or tuning, no arrangement or production ("drops out", "builds"), no genre label, no bar counts. If a sentence is about how the song sounds or is built, delete it.`;

/** The sung words of a lyrics text: tag, cue, header and metadata lines removed. */
export function sungLyrics(text: string): string {
  return (text || '')
    .split('\n')
    .map(l => l.trim())
    .filter(l => l !== '' && !/^[[{(]/.test(l) && !/^(?:META_|MIX_|\/\/\/)/.test(l) && !/^NO\s+VOCALS\b/i.test(l))
    .join('\n');
}

const j = (...parts: string[]) => new RegExp(`\\b(?:${parts.join('|')})\\b`, 'i');

/** Kinds of word a lyrics-only summary must not contain (unless the lyrics themselves use the word). */
const BANNED: RegExp[] = [
  // song sections and meta talk
  j('verses?', 'pre-?chorus(?:es)?', 'chorus(?:es)?', 'hooks?', 'bridges?', 'pivots?', 'outros?', 'intros?', 'interludes?', 'refrains?', 'breakdowns?', 'solos?'),
  j("the\\s+song(?:'s|’s)?", 'this\\s+song', 'the\\s+track', 'the\\s+narrator', 'the\\s+lyrics', 'the\\s+record'),
  // instruments and gear
  j('guitars?', 'bassist', 'bass\\s+(?:line|guitar|drum)', 'drums?', 'drummer', 'kick\\s+drum', 'snare\\s+drum', 'pianos?', 'organs?', 'synths?', 'synthesizers?', 'banjo', 'fiddle', 'mandolin', 'dobro', 'harmonica', 'pedal\\s+steel', 'saxophone', 'trumpet', 'riffs?', 'chugs?', 'chugging', 'palm[- ]?mut\\w*', '(?:\\d|six|seven|eight)-string'),
  /\bdrop[- ]?[a-g]\b/i,
  // sound, tempo, arrangement
  j('reverb', 'distortion', 'distorted', 'amps?', 'amplifiers?', 'tuning', 'tempo', 'bpm', 'crescendo', 'a\\s+cappella', 'wall\\s+of\\s+sound', 'behind\\s+the\\s+beat', 'on\\s+the\\s+beat', 'beat\\s+drops?'),
  j('(?:band|wall|music|drums?|guitars?)\\s+(?:drops?|falls?|cuts?)\\s+out'),
  // key and harmony
  /\b[a-g][#b♯♭]?[- ]?(?:major|minor)\b/i,
  j('key\\s+(?:of|change)', 'minor\\s+key', 'major\\s+key', 'modulat\\w+', 'chords?', 'melod(?:y|ies|ic)', 'harmon(?:y|ies|ic)', 'octaves?', 'falsetto'),
  // the voice as sound
  j('vocals?', 'vocalists?', 'baritone', 'tenor', 'soprano', 'gravelly', 'raspy', 'husky\\s+voice', 'voice\\s+carr(?:y|ies)'),
  // genre labels
  j('metal', 'djent', 'sludge', 'hip-?hop', 'boom-?bap', 'edm', 'r&b', 'motown', 'bluegrass', 'honky-?tonk', 'grunge', 'punk'),
  // production and structure maths
  j('production', 'arrangement', 'instrumentation', 'orchestration', 'mastering', '\\d+\\s*bars?', 'phi', 'fibonacci', 'golden\\s+ratio'),
];

const uniq = (xs: string[]) => [...new Set(xs)];

/** The banned words found in `text` that the sung lyrics do not use themselves. */
export function findSummaryViolations(summary: string, lyrics: string): string[] {
  const sung = lyrics || '';
  const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // The word itself (its plural or "-ing" form counts too) appears in the sung lyrics.
  const lyricsUse = (word: string) => {
    const stem = word.replace(/s$/, '');
    return sung !== '' && new RegExp(`\\b${escapeRe(stem)}`, 'i').test(sung);
  };
  const found: string[] = [];
  for (const re of BANNED) {
    const matches = (summary || '').match(new RegExp(re.source, 'gi'));
    if (!matches) continue;
    // Judged word by word: "guitar" in the lyrics does not excuse "piano" in the summary.
    for (const m of matches) if (!lyricsUse(m.toLowerCase())) found.push(m.toLowerCase());
  }
  return uniq(found);
}

const sentencesOf = (text: string): string[] => text.split(/(?<=[.!?])\s+/).map(s => s.trim()).filter(Boolean);

/**
 * Last resort when no model can rewrite it: drop every sentence that breaks the rule. A summary that is left
 * too short to mean anything becomes empty; an empty summary is better than a wrong one.
 */
export function stripViolatingSentences(summary: string, lyrics: string): string {
  const kept = sentencesOf(summary || '').filter(s => findSummaryViolations(s, lyrics).length === 0);
  const text = kept.join(' ').trim();
  return text.length >= 40 ? text : '';
}

/** The story-only rewrite request, built from the one rule. `lyrics` should be the sung lyrics. */
export function buildSummaryRewriteInstruction(args: { lyrics: string; current: string; badWords: string[]; attempt: number }): string {
  const { lyrics, current, badWords, attempt } = args;
  return `Rewrite the summary of the song below as a plain retelling of what happens to the people in it, and what it means to them.

Rules:
- 2-3 sentences, under 400 characters.
- ${SUMMARY_RULE}
- Do not use any of these words: ${badWords.join(', ')}.${attempt > 0 ? ' (Your last attempt still used some of them.)' : ''}
- No quoted lyric lines, no comparisons to unrelated objects.

Reply with the summary text only -- no label, no quotes, no explanation.

LYRICS:
${lyrics.slice(0, 5000)}

CURRENT SUMMARY (do not reuse its wording where it describes how the song sounds or is built):
${current}`;
}

/** Cleans a model reply down to the bare summary text. */
export function cleanSummaryReply(reply: string): string {
  return (reply || '')
    .replace(/^```[a-z]*\n?|\n?```$/g, '')
    .replace(/^\s*(?:summary|rewrite[d]?)\s*:\s*/i, '')
    .trim()
    .replace(/^["“]+|["”]+$/g, '')
    .trim();
}
